import { Box, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import React, { ReactNode } from "react";
import { Draggable } from "react-beautiful-dnd";
import { AppReportTaskTypeTitlePageType, TaskDataRowDataType } from "ui/Scripts/source/models/enums";
import { ITaskTypeCol, TaskTypeCol } from "ui/Scripts/source/models/task/taskTypeCol";
import { Translations } from "ui/Scripts/source/models/translations";

/* eslint-disable no-unused-vars */
export const enum TaskFormDesignerComponentType {
    Title1 = "title1",
    Title2 = "title2",
    Title3 = "title3",
    Title4 = "title4",
    NormalText = "title5",
    TextInput = "textInput",
    TextArea = "textArea",
    Date = "date",
    Time = "time",
    TimeSpan = "timeSpan",
    // DateTime = "dateTime",
    Checkbox = "checkbox",
    Radio = "radio",
    Dropdown = "dropdown",
    Decimal = "decimal",
    Integer = "integer",
    NumberCalculated = "numberCalculated",
    IntegerCalculated = "integerCalculated",
    // Attachment = "attachment",
    LineBreak = "lineBreak",
    EmptyArea = "emptyArea",
    // PageBreak = "pageBreak",
    Signature = "signature",
    CoverPage = "coverPage",
    TitlePage = "titlePage",
    PageBreak = "pageBreak",
    RichText = "richText",
    DocumentReference = "documentReference",
    EventReference = "eventReference",
    TaskReference = "taskReference",
    RecordReference = "recordReference"
}
/* eslint-enable no-unused-vars */

/* eslint-disable no-unused-vars */
export const enum TaskFormDesignerSectionType {
    Page = "page",
    Title = "title",
    Text = "text",
    TextInput = "textInput",
    DateAndTime = "dateAndTime",
    Boolean = "boolean",
    Selection = "selection",
    Number = "number",
    Calculated = "calculated",
    Break = "break",
    Other = "image",
    DataReference = "dataReference"
}
/* eslint-enable no-unused-vars */

const ListItemButtonContent = ({
    componentType,
    title,
    icon,
    classes
}: IFormDesignerComponentPickerItemProps) => {
    const isLabel = componentType === TaskFormDesignerComponentType.Title1 ||
        componentType === TaskFormDesignerComponentType.Title2 ||
        componentType === TaskFormDesignerComponentType.Title3 ||
        componentType === TaskFormDesignerComponentType.Title4 ||
        componentType === TaskFormDesignerComponentType.NormalText;
    return (
        <>
            <ListItemIcon>{icon}</ListItemIcon>
            {isLabel
                ? (<div className="jsonFormData">
                    <ListItemText sx={componentType === TaskFormDesignerComponentType.NormalText ? { fontSize: "14px" } : null}><span className={classes}><span className="label">{title}</span></span></ListItemText>
                </div>)
                : (<ListItemText>{title}</ListItemText>)
            }
        </>
    );
};

export interface IFormDesignerComponentPickerItemProps {
    componentType: TaskFormDesignerComponentType;
    index: number;
    title: string;
    icon: ReactNode;
    classes?: string;
    section: TaskFormDesignerSectionType;
}

// eslint-disable-next-line react/display-name
export const getRenderItem = (items: IFormDesignerComponentPickerItemProps[]) => (provided, _snapshot, rubric) => {
    const item = items[rubric.source.index];
    return (
        <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
        >
            <ListItemButton
                sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    transition: "0.001s !important",
                }}
            >
                <ListItemButtonContent {...item} />
            </ListItemButton>
        </div>
    );
};

export const FormDesignerComponentPickerItem = (props: IFormDesignerComponentPickerItemProps) => {
    const renderItem = getRenderItem([props]);

    return (
        <Box sx={{ height: "42px" }}>
            <ListItemButton
                sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    position: "absolute",
                    width: "100%",
                }}
            >
                <ListItemButtonContent {...props} />
            </ListItemButton>
            <Draggable
                draggableId={props.componentType}
                key={props.componentType}
                index={props.index}
            >
                {renderItem}
            </Draggable>
        </Box>
    );
};

export const getTaskFormDesignerComponentType = (taskTypeCol: ITaskTypeCol): TaskFormDesignerComponentType => {
    const classes = taskTypeCol.class;
    const isTextAreaComponent = taskTypeCol.dataType === TaskDataRowDataType.String && taskTypeCol.maxLength > 499;
    const isDropdown = taskTypeCol.options?.length > 0 || taskTypeCol.idOptions?.length > 0;
    const isCalulatedField = TaskTypeCol.isCalculatedField(taskTypeCol);
    if (taskTypeCol.dataType === TaskDataRowDataType.Label) {
        return classes.includes("eLabel")
            ? TaskFormDesignerComponentType.Title1
            : (classes.includes("lLabel")
                ? TaskFormDesignerComponentType.Title2
                : (classes.includes("mLabel")
                    ? TaskFormDesignerComponentType.Title3
                    : (classes.includes("sLabel")
                        ? TaskFormDesignerComponentType.Title4
                        : (taskTypeCol.title
                            ? TaskFormDesignerComponentType.NormalText
                            : TaskFormDesignerComponentType.EmptyArea
                        ))));
    }
    if (taskTypeCol.dataType === TaskDataRowDataType.String) {
        return !isDropdown
            ? (isTextAreaComponent
                ? TaskFormDesignerComponentType.TextArea
                : TaskFormDesignerComponentType.TextInput)
            : (classes.includes("radio")
                ? TaskFormDesignerComponentType.Radio
                : TaskFormDesignerComponentType.Dropdown
            );
    }
    if (taskTypeCol.dataType === TaskDataRowDataType.Number) {
        return !isCalulatedField
            ? TaskFormDesignerComponentType.Decimal
            : TaskFormDesignerComponentType.NumberCalculated;
    }
    if (taskTypeCol.dataType === TaskDataRowDataType.Integer) {
        return !isCalulatedField
            ? TaskFormDesignerComponentType.Integer
            : TaskFormDesignerComponentType.IntegerCalculated;
    }
    if (taskTypeCol.dataType === TaskDataRowDataType.Date) {
        return TaskFormDesignerComponentType.Date;
    }
    if (taskTypeCol.dataType === TaskDataRowDataType.Time) {
        return TaskFormDesignerComponentType.Time;
    }
    if (taskTypeCol.dataType === TaskDataRowDataType.TimeSpan) {
        return TaskFormDesignerComponentType.TimeSpan;
    }
    if (taskTypeCol.dataType === TaskDataRowDataType.Boolean) {
        return TaskFormDesignerComponentType.Checkbox;
    }
    if (taskTypeCol.dataType === TaskDataRowDataType.LineBreak) {
        return TaskFormDesignerComponentType.LineBreak;
    }
    if (taskTypeCol.dataType === TaskDataRowDataType.Signature) {
        return TaskFormDesignerComponentType.Signature;
    }
    if (taskTypeCol.dataType === TaskDataRowDataType.TitlePage) {
        return taskTypeCol.objectValue?.type === AppReportTaskTypeTitlePageType.CoverPage
            ? TaskFormDesignerComponentType.CoverPage
            : TaskFormDesignerComponentType.TitlePage;
    }
    if (taskTypeCol.dataType === TaskDataRowDataType.PageBreak) {
        return TaskFormDesignerComponentType.PageBreak;
    }
    if (taskTypeCol.dataType === TaskDataRowDataType.RichText) {
        return TaskFormDesignerComponentType.RichText;
    }
    if (taskTypeCol.dataType === TaskDataRowDataType.DocumentReference) {
        return TaskFormDesignerComponentType.DocumentReference;
    }
    if (taskTypeCol.dataType === TaskDataRowDataType.EventReference) {
        return TaskFormDesignerComponentType.EventReference;
    }
    if (taskTypeCol.dataType === TaskDataRowDataType.TaskReference) {
        return TaskFormDesignerComponentType.TaskReference;
    }
    if (taskTypeCol.dataType === TaskDataRowDataType.RecordReference) {
        return TaskFormDesignerComponentType.RecordReference;
    }
    return TaskFormDesignerComponentType.TextInput;
};

export const getTaskFormDesignerComponentTitle = (taskTypeCol: ITaskTypeCol) => {
    const designerComponentType = getTaskFormDesignerComponentType(taskTypeCol);
    switch (designerComponentType) {
    case TaskFormDesignerComponentType.Title1:
        return Translations.FormTitle1;
    case TaskFormDesignerComponentType.Title2:
        return Translations.FormTitle2;
    case TaskFormDesignerComponentType.Title3:
        return Translations.FormTitle3;
    case TaskFormDesignerComponentType.Title4:
        return Translations.FormTitle4;
    case TaskFormDesignerComponentType.NormalText:
        return Translations.FormNormalText;
    case TaskFormDesignerComponentType.TextArea:
        return Translations.FormTextArea;
    case TaskFormDesignerComponentType.TextInput:
        return Translations.FormTextInput;
    case TaskFormDesignerComponentType.Dropdown:
        return Translations.FormDropdown;
    case TaskFormDesignerComponentType.Decimal:
        return Translations.FormDecimalNumber;
    case TaskFormDesignerComponentType.Integer:
        return Translations.FormIntegerNumber;
    case TaskFormDesignerComponentType.NumberCalculated:
        return Translations.FormCalculatedDecimalNumber;
    case TaskFormDesignerComponentType.IntegerCalculated:
        return Translations.FormCalculatedIntegerNumber;
    case TaskFormDesignerComponentType.Date:
        return Translations.FormDate;
    case TaskFormDesignerComponentType.Time:
        return Translations.FormTime;
    case TaskFormDesignerComponentType.TimeSpan:
        return Translations.FormTimeSpan;
    case TaskFormDesignerComponentType.Checkbox:
        return Translations.FormCheckbox;
    case TaskFormDesignerComponentType.Radio:
        return Translations.FormRadio;
    case TaskFormDesignerComponentType.LineBreak:
        return Translations.FormLineBreak;
    case TaskFormDesignerComponentType.EmptyArea:
        return Translations.FormEmptyArea;
    case TaskFormDesignerComponentType.Signature:
        return Translations.FormSignature;
    case TaskFormDesignerComponentType.CoverPage:
        return Translations.FormCoverPage;
    case TaskFormDesignerComponentType.TitlePage:
        return Translations.FormTitlePage;
    case TaskFormDesignerComponentType.PageBreak:
        return Translations.FormPageBreak;
    case TaskFormDesignerComponentType.RichText:
        return Translations.FormNormalText;
    case TaskFormDesignerComponentType.DocumentReference:
        return Translations.FormDocumentReference;
    case TaskFormDesignerComponentType.EventReference:
        return Translations.FormEventReference;
    case TaskFormDesignerComponentType.TaskReference:
        return Translations.FormTaskReference;
    default:
        return "";
    }
};